import { apiFiles } from "@/api/file.api.ts";
import { ColorBlock } from "@/components/colorblock/ColorBlock.tsx";
import { limitedColors } from "@/components/emoji/limited-colors.ts";
import { BlogPostEditAttachments } from "@/pages/blog/edit/components/BlogPostEditAttachments.tsx";
import { RichEditor } from "@/components/rich-editor/RichEditor.tsx";
import { RichEditorBodyFileType } from "@/components/rich-editor/rich-editor.types.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useFileDropzone } from "@/hooks/use-file-dropzone.hook.ts";
import { useMentionsHook } from "@/hooks/use-mention.hook.ts";
import i18n from "@/i18n.ts";
import { useBlogPostDetails } from "@/queries/blog.query.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import {
  ApiBlogPostMock,
  BlogEditSteps,
  WebpUploadableImageFileTypes,
  useBlogEditContext,
} from "@/types/blog.types.ts";
import { ApiFilePathTypes } from "@/types/file.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  FileDropzone,
  FullScreenPage,
  Input,
  Stack,
  Switch,
  Textarea,
  Title,
  toColorName,
  useToasts,
} from "@skailer-com/uikit";
import { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";

const BODY_FILE_MAX_SIZE_MB = 25;
const BODY_FILE_MIME_TYPES: Record<RichEditorBodyFileType, string[]> = {
  video: ["video/mp4", "video/webm"],
  pdf: ["application/pdf"],
};

const buildBlogPostEditSchema = () =>
  yup.object({
    title: yup.string().required(i18n.t("errors:field_required")),
    caption: yup.string(),
    without_image: yup.boolean(),
    body: yup.string(),
    color: yup.string(),
  });

export function BlogPostEditForm() {
  const { t } = useTranslation();
  const { setStep, postId, setPost, post, setWithoutImage, registerTemporaryFile } =
    useBlogEditContext();
  const isEdit = !!postId;
  const navigate = useNavigate();
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isAttachmentUploading, setIsAttachmentUploading] = useState(false);
  const [isBodyFileUploading, setIsBodyFileUploading] = useState(false);
  const { showError } = useToasts();
  const { data: employeeMe } = useEmployeeMe();
  const { handleErrors } = useErrorHandle();
  const { data: postDetails } = useBlogPostDetails(Number(postId), !!postId);
  const { replaceMentions } = useMentionsHook();
  const supportedFormats = [".jpg", ".png", ".gif"].join(", ");

  useEffect(() => {
    if (postDetails && (!post || post.id === 0)) {
      setPost(postDetails);
    } else if (!post && !postId) {
      setPost(ApiBlogPostMock);
    }
  }, [postDetails, post, postId, setPost]);

  const blogPostEditSchema = buildBlogPostEditSchema();

  const {
    control,
    register,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    resolver: yupResolver(blogPostEditSchema),
    values: {
      title: post?.title ?? "",
      caption: post?.caption ?? "",
      without_image: !!post?.color && !post.hero_image_file,
      body: post?.body ? replaceMentions(post?.body, post?.mentioned_employees) : "",
      color: toColorName(post?.color) ?? post?.color ?? "blue",
    },
  });

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const formData = new FormData();
        formData.append("file", file);
        formData.append("path", ApiFilePathTypes.blog_post);
        formData.append("is_public", "true");
        formData.append("save_original_name", "true");

        setIsUploading(true);

        const { data, error } = await apiFiles.add(formData);

        if (data) {
          const { data: signedLink, error } = await apiFiles.getLink(data.uuid, 1);

          if (!signedLink) return handleErrors(error);

          if (post)
            setPost({
              ...post,
              hero_image_file: {
                file_uuid: data.uuid,
                url: signedLink.download_url,
                color_palette: data.color_palette ?? [],
              },
            });
        } else {
          handleErrors(error);
        }

        setIsUploading(false);
      }
    },
    [post]
  );

  const onSubmit = handleSubmit((data) => {
    if (post) {
      setPost({
        ...post,
        title: data.title,
        caption: data.caption ?? "",
        body: data.body ?? "",
        author: post.author ?? employeeMe,
        color: data.color,
        hero_image_file: !withoutImageWatch ? post.hero_image_file : null,
      });
    }

    setStep(BlogEditSteps.preview);
  });

  const withoutImageWatch = watch("without_image");
  const { onDropRejected } = useFileDropzone({
    tooManyFilesMessage: t("errors:only_one_file_allowed", {
      formats: supportedFormats,
    }),
    unsupportedFileFormatMessage: t("errors:unsupported_file_format", {
      formats: supportedFormats,
    }),
  });

  const uploadImage = async (file: File) => {
    const formData = new FormData();

    formData.append("file", file);
    formData.append("path", ApiFilePathTypes.blog_post);
    formData.append("is_public", "true");
    formData.append("save_original_name", "true");

    const { data } = await apiFiles.add(formData);

    return data;
  };

  const uploadBodyFile = async (file: File, type: RichEditorBodyFileType) => {
    const isVideo = type === "video";
    if (!BODY_FILE_MIME_TYPES[type].includes(file.type)) {
      showError({
        title: t(isVideo ? "blog:video_unsupported_format" : "blog:pdf_unsupported_format"),
      });
      return;
    }
    if (file.size > BODY_FILE_MAX_SIZE_MB * 1024 * 1024) {
      showError({
        title: t(isVideo ? "blog:video_too_large" : "blog:pdf_too_large", {
          size: BODY_FILE_MAX_SIZE_MB,
        }),
        description: isVideo ? t("blog:video_too_large_hint") : undefined,
      });
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("path", ApiFilePathTypes.blog_attachment);
    formData.append("is_public", "false");
    formData.append("generate_public_link", "false");
    formData.append("save_original_name", "true");

    setIsBodyFileUploading(true);
    const { data, error } = await apiFiles.add(formData);
    setIsBodyFileUploading(false);

    if (!data) {
      handleErrors(error);
      return;
    }
    registerTemporaryFile(data.uuid);
    return data;
  };

  const isUploadingBlocked = isAttachmentUploading || isBodyFileUploading;

  return (
    <FullScreenPage
      title={!isEdit ? t("blog:creating_post") : t("blog:editing_post")}
      backSlot={
        <Button
          variant={"secondary"}
          size={"lg"}
          onClick={() => {
            navigate(-1);
          }}
        >
          {t("common:exit")}
        </Button>
      }
      actionsSlot={
        <Stack direction={"horizontal"} gap={"sm"}>
          <Button size={"lg"} onClick={onSubmit} disabled={isUploadingBlocked}>
            {t("core:preview")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Stack gap={"sm"} justify={"center"} className={"container-sm ml-auto mr-auto grow"}>
          <Stack className={"card-panel"} gap={"lg"}>
            <Title
              header={!isEdit ? t("blog:creating_post") : t("blog:editing_post")}
              size={"lg"}
            />
            <Input
              type={"text"}
              label={t("blog:post_title")}
              size={"lg"}
              {...register("title")}
              error={errors.title?.message}
            />
            <Textarea
              label={t("blog:post_caption")}
              {...register("caption")}
              error={errors.caption?.message}
            />
          </Stack>
          <Stack className={"card-panel"} gap={"2xl"}>
            <Title header={t("blog:hero_image")} />
            <Stack gap={"md"}>
              <Controller
                control={control}
                name={"without_image"}
                render={({ field: { value, onChange } }) => (
                  <Switch
                    checked={value}
                    onChange={(value) => {
                      setWithoutImage(value);
                      onChange(value);
                    }}
                    title={t("blog:post_without_image")}
                  />
                )}
              />
              {withoutImageWatch ? (
                <Stack gap={"md"}>
                  <Title
                    header={t("blog:choose_bg_color")}
                    caption={t("blog:for_preview")}
                    size={"sm"}
                  />
                  <Controller
                    control={control}
                    name={"color"}
                    render={({ field: { value, onChange } }) => (
                      <Stack direction={"horizontal"} gap={"sm"} className={"h-12"}>
                        {limitedColors.map((colorName) => (
                          <ColorBlock
                            key={colorName}
                            color={colorName}
                            size={"lg"}
                            selected={toColorName(value) === colorName}
                            onClick={() => onChange(colorName)}
                          />
                        ))}
                      </Stack>
                    )}
                  ></Controller>
                </Stack>
              ) : (
                <FileDropzone
                  className={"min-h-96"}
                  onDrop={onDrop}
                  accept={Object.fromEntries(
                    Object.entries(WebpUploadableImageFileTypes).map(([key, value]) => [
                      value,
                      [`.${key.toLowerCase()}`],
                    ])
                  )}
                  onDropRejected={onDropRejected}
                  multiple={false}
                  maxFiles={1}
                  description={t("common:accepts_formats", {
                    formats: supportedFormats,
                  })}
                  ctaActionText={t("common:image").toLowerCase()}
                  activeDescription={t("common:upload_file_drag_and_drop")}
                  isLoading={isUploading}
                >
                  {post?.hero_image_file?.url ? (
                    <img
                      src={post.hero_image_file.url}
                      className={"block min-h-96 w-full object-cover"}
                    />
                  ) : undefined}
                </FileDropzone>
              )}
            </Stack>
          </Stack>
          <Stack className={"card-panel"} gap={"2xl"}>
            <Title header={t("blog:post_body")} />

            <Controller
              control={control}
              name={"body"}
              render={({ field: { value, onChange } }) => (
                <RichEditor
                  value={value ?? ""}
                  onChange={onChange}
                  minHeight={220}
                  toolbarClassName={"sticky top-20 bg-light z-10"}
                  uploadImage={uploadImage}
                  uploadBodyFile={uploadBodyFile}
                  placeholder={t("blog:enter_post_content")}
                  insertEmojiIsOn={true}
                />
              )}
            />
          </Stack>
          <BlogPostEditAttachments onUploadingChange={setIsAttachmentUploading} />
          <Stack className="pb-(--gap-3xl)">
            <ServerErrorField errors={errors} />
            <Button size={"lg"} onClick={onSubmit} disabled={isUploadingBlocked}>
              {t("core:preview")}
            </Button>
          </Stack>
        </Stack>
      </form>
    </FullScreenPage>
  );
}
